import { useCallback } from 'react';
import useLoginAuth from '../useLoginAuth';
import useImageProcessor from './useImageProcessor';
import useImageUpload from './useCommonImageUpload';
import { v4 as uuidv4 } from 'uuid';
import { useLatest } from 'react-use';
import type { BaseImageStatus } from '@/components/batchToolsComp/layoutContext/ImageProcessingContext';
import { getLocalImageSize } from '@/utils/getLocalImageSize';
import { feBot } from '@/monitor';

/**
 * 公共图像上传Hook的参数接口
 */
export interface UseImageUploaderProps<T extends BaseImageStatus> {
  from?: string;
  ImageState: { images: Map<string, T> };
  dispatchImage: React.Dispatch<any>;
  createInitialImageStatus: (file: File, imageId: string) => T;
}

/**
 * 公共图像上传Hook - 组合各种图像处理功能
 * @description 提供完整的图像上传流程：预处理 -> 状态管理 -> 上传
 */
export function useCommonImageUploader<T extends BaseImageStatus>(
  props: UseImageUploaderProps<T>
) {
  const {
    from = 'ImageUploader',
    ImageState,
    dispatchImage,
    createInitialImageStatus,
  } = props;
  const latestImageStateRef = useLatest(ImageState);

  useLoginAuth({ from: from + '_upload' });

  const { processImageFiles, validateAndCreateImageUrl } =
    useImageProcessor(from);
  const { uploadImages } = useImageUpload();

  /**
   * 检查并处理上传的图像文件
   */
  const checkUploadImage = useCallback(
    async (fileList: FileList) => {
      const existingImages = Array.from(
        latestImageStateRef.current.images.values()
      );
      return await processImageFiles(fileList, existingImages);
    },
    [processImageFiles, latestImageStateRef]
  );

  /**
   * 生成图像映射项
   */
  const generateImageMapItems = useCallback(
    async (finalFileList: File[]) => {
      try {
        if (finalFileList.length === 0) return;

        // 准备批量添加的数据
        const imageBatch: Array<{ id: string; status: T }> = [];
        const loadPromises: Promise<{
          id: string;
          success: boolean;
          url?: string;
          error?: string;
        }>[] = [];

        for (const file of finalFileList) {
          const imageId = uuidv4();
          const imageStatus = createInitialImageStatus(file, imageId);
          const imageSize = await getLocalImageSize(file);
          imageStatus.localImgSize = imageSize;
          imageBatch.push({ id: imageId, status: imageStatus });
          loadPromises.push(
            validateAndCreateImageUrl(file).then((result) => ({
              id: imageId,
              ...result,
            }))
          );
        }

        // 批量添加初始状态
        dispatchImage({ type: 'ADD_IMAGES_BATCH', payload: imageBatch });

        // 验证所有图像
        const results = await Promise.all(loadPromises);

        // 批量更新验证结果
        const updateBatch = results.map((result) => ({
          id: result.id,
          status: result.success
            ? { localImgUrl: result.url, generateStatus: 'success' }
            : {
                generateStatus: 'failed',
                generateStatusText: result.error,
                localImgUrl: '',
              },
        }));

        dispatchImage({ type: 'UPDATE_IMAGES_BATCH', payload: updateBatch });
      } catch (error) {
        feBot.lark_report({
          title: '[FE_ERROR] generateImageMapItems ',
          type: from,
          errorInfo: JSON.stringify(error),
          isRPostHog: true,
        });
      }
    },
    [dispatchImage, createInitialImageStatus, validateAndCreateImageUrl, from]
  );

  /**
   * 上传原始图像
   */
  const uploadOriginalImages = useCallback(async () => {
    // 筛选需要上传的图像
    const imagesToUpload = Array.from(ImageState.images.values()).filter(
      (status) =>
        status.generateStatus === 'success' &&
        !status.uploadUrl &&
        !status.uploadId &&
        status.localFile
    );

    if (imagesToUpload.length === 0) return;

    // 使用公共上传Hook
    await uploadImages(
      imagesToUpload,
      from,
      // 进度回调
      (imageId, status, message) => {
        dispatchImage({
          type: 'UPDATE_IMAGE',
          payload: {
            id: imageId,
            status: { uploadStatus: status, uploadStatusText: message },
          },
        });
      },
      // 完成回调
      (imageId, result) => {
        if (result.success) {
          dispatchImage({
            type: 'UPDATE_IMAGE',
            payload: {
              id: imageId,
              status: {
                uploadStatus: 'success',
                uploadStatusText: 'upload success',
                uploadUrl: result.url,
                uploadId: result.fileId,
              },
            },
          });
        } else {
          dispatchImage({
            type: 'UPDATE_IMAGE',
            payload: {
              id: imageId,
              status: {
                uploadStatus: 'failed',
                uploadStatusText: result.error,
              },
            },
          });
        }
      }
    );
  }, [ImageState.images, dispatchImage, from, uploadImages]);

  /**
   * 处理图像:用户选择完图像后，对图像进行检测、本地初始化和缓存
   */
  const handleImageUploadInit = useCallback(
    async (fileList: FileList) => {
      try {
        const finalFileList = await checkUploadImage(fileList);
        await generateImageMapItems(finalFileList);
      } catch (error) {
        console.error('handleImageUpload error:', error);
        feBot.lark_report({
          title: '[FE_ERROR] handleImageUploadInit ',
          type: from,
          errorInfo: JSON.stringify(error),
          isRPostHog: true,
        });
      }
    },
    [checkUploadImage, from, generateImageMapItems]
  );

  return { uploadOriginalImages, handleImageUploadInit };
}

export default useCommonImageUploader;
